/**
 * Abrechnungsrelevanter Zustand eines Projekts.
 *
 * Blattmodul ohne Importe: die Buchhaltungsansicht, die Belegliste und die
 * Hinweise lesen denselben Status, und er stand bisher an jeder Stelle als
 * eigene Kopie derselben Bedingung.
 */

/**
 * Ein Projekt, soweit sein Status hier interessiert.
 *
 * ZWEI Quellen, und beide zaehlen: die Spalte `projects.status` und der Status
 * im JSON-Dokument. Sie koennen auseinanderlaufen — ein Projekt, das nur im
 * Dokument abgeschlossen wurde, traegt die Spalte noch auf 'active'. Wer nur
 * eine Seite liest, haelt ein abgeschlossenes Projekt fuer laufend.
 */
export interface ProjectStatusSource {
    status?: string | null;
    data?: { metadata?: { globalData?: { status?: string | null } } | null } | null;
}

const hasStatus = (project: ProjectStatusSource | null | undefined, wanted: string): boolean =>
    project?.status === wanted || project?.data?.metadata?.globalData?.status === wanted;

/** BEENDET ('completed'): Arbeit vorbei, Schlussrechnung steht noch aus. */
export const isProjectCompleted = (project: ProjectStatusSource | null | undefined): boolean =>
    hasStatus(project, 'completed');

/** ARCHIV ('finished'): Schlussrechnung gestellt und bezahlt, Projekt liegt still. */
export const isProjectArchived = (project: ProjectStatusSource | null | undefined): boolean =>
    hasStatus(project, 'finished');

/**
 * Ist das Projekt abgeschlossen — also faellig fuer die Schlussrechnung?
 *
 * Beide Endzustaende zaehlen: BEENDET ist der Regelfall (die Schlussrechnung
 * wird jetzt geschrieben), ARCHIV der Nachzuegler (eine spaete Gutschrift oder
 * Nachtragsrechnung an einem archivierten Projekt bleibt eine Schlussrechnung).
 */
export const isProjectFinished = (project: ProjectStatusSource | null | undefined): boolean =>
    isProjectCompleted(project) || isProjectArchived(project);

/** Statusnamen, wie die Oberflaeche sie zeigt (Projekte, Verwaltung, Cockpit). */
export const PROJECT_STATUS_LABELS: Record<string, string> = {
    planning: 'Planung',
    active: 'Ausführung',
    on_hold: 'Ruht',
    completed: 'Beendet',
    finished: 'Archiv',
};

export const projectStatusLabel = (status: string | null | undefined): string =>
    PROJECT_STATUS_LABELS[status || ''] || status || 'Planung';

/**
 * Die Belegart, mit der der Anlagedialog fuer dieses Projekt aufgeht.
 *
 * Ein abgeschlossenes Projekt bekommt eine SCHLUSSRECHNUNG. Sie ist die
 * letzte Rechnung des Projekts: sie nimmt alle noch offenen Positionen und
 * zieht die bereits gestellten Abschlaege je Steuersatz ab. Stand dort
 * "Rechnung", entstand ein Beleg ohne diesen Abzugsblock — die Abschlaege
 * blieben ungefordert stehen, und der Kunde bekam eine Rechnung ueber Betraege,
 * die er laengst gezahlt hatte.
 *
 * Eine Vorbelegung, keine Festlegung: im Dialog bleibt jede Belegart waehlbar.
 * Ein abgeschlossenes Projekt kann eine Gutschrift oder eine nachtraegliche
 * Teilrechnung brauchen, und das zu verbieten waere schlimmer als der Fehlgriff,
 * den die Vorbelegung erspart.
 */
export const billingDocTypeForProject = (
    project: ProjectStatusSource | null | undefined,
): 'INVOICE' | 'FINAL_INVOICE' => (isProjectFinished(project) ? 'FINAL_INVOICE' : 'INVOICE');
